import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import {
  User, Lock, Phone, Mail, Shield, Save, Loader2, Eye, EyeOff,
  CheckCircle2, Sun, Moon, Monitor, LogOut, Trash2,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";

function passwordStrength(pw: string): { score: number; label: string; color: string } {
  if (!pw) return { score: 0, label: "", color: "" };
  let s = 0;
  if (pw.length >= 8)  s++;
  if (pw.length >= 12) s++;
  if (/[A-Z]/.test(pw)) s++;
  if (/[0-9]/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  if (s <= 1) return { score: s, label: "Weak",   color: "bg-red-500" };
  if (s <= 3) return { score: s, label: "Fair",   color: "bg-amber-500" };
  return            { score: s, label: "Strong", color: "bg-green-500" };
}

export default function Profile() {
  const { user, profile, logout } = useAuth();
  const { toast } = useToast();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Personal info
  const [fullName, setFullName]           = useState("");
  const [phone, setPhone]                 = useState("");
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  // Email change
  const [newEmail, setNewEmail]           = useState("");
  const [emailChangeSent, setEmailChangeSent] = useState(false);
  const [isChangingEmail, setIsChangingEmail] = useState(false);

  // Password change
  const [newPassword, setNewPassword]     = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew]             = useState(false);
  const [showConfirm, setShowConfirm]     = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);

  // Delete account
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [isDeleting, setIsDeleting]       = useState(false);

  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name ?? "");
      setPhone(profile.phone ?? "");
    } else if (user) {
      setFullName(user.fullName ?? "");
    }
  }, [profile, user]);

  // Handle redirect back from Supabase email-change confirmation
  useEffect(() => {
    if (searchParams.get("email_changed") === "1") {
      toast({ title: "Email updated", description: "Your email address has been changed successfully." });
      setSearchParams({}, { replace: true });
    }
  }, []);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleSaveProfile = async () => {
    if (!user || !fullName.trim()) return;
    setIsSavingProfile(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ full_name: fullName.trim(), phone: phone.trim() || null })
        .eq("id", user.id);
      if (error) throw error;
      toast({ title: "Profile updated", description: "Your changes have been saved." });
    } catch (err: any) {
      toast({ title: "Error", description: err.message ?? "Failed to update profile.", variant: "destructive" });
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleChangeEmail = async () => {
    if (!newEmail || newEmail === user?.email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      toast({ title: "Invalid email", description: "Please enter a valid email address.", variant: "destructive" });
      return;
    }
    setIsChangingEmail(true);
    try {
      const { error } = await supabase.auth.updateUser(
        { email: newEmail },
        { emailRedirectTo: `${window.location.origin}/profile?email_changed=1` },
      );
      if (error) throw error;
      setEmailChangeSent(true);
      setNewEmail("");
    } catch (err: any) {
      toast({ title: "Error", description: err.message ?? "Failed to send confirmation.", variant: "destructive" });
    } finally {
      setIsChangingEmail(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast({ title: "Passwords don't match", variant: "destructive" });
      return;
    }
    if (newPassword.length < 8) {
      toast({ title: "Password too short", description: "Must be at least 8 characters.", variant: "destructive" });
      return;
    }
    setIsSavingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      toast({ title: "Password changed", description: "Your password has been updated successfully." });
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: any) {
      toast({ title: "Error", description: err.message ?? "Failed to change password.", variant: "destructive" });
    } finally {
      setIsSavingPassword(false);
    }
  };

  const handleSignOutEverywhere = async () => {
    await supabase.auth.signOut({ scope: "global" });
    navigate("/login");
    toast({ title: "Signed out everywhere", description: "All active sessions have been terminated." });
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== "DELETE") return;
    setIsDeleting(true);
    try {
      const { error } = await supabase.functions.invoke("delete-account");
      if (error) throw error;
      await logout();
      navigate("/");
    } catch (err: any) {
      toast({ title: "Error", description: err.message ?? "Failed to delete account.", variant: "destructive" });
      setIsDeleting(false);
    }
  };

  const strength  = passwordStrength(newPassword);
  const roleLabel = user?.role?.replace(/_/g, " ") ?? "—";
  const initials  = fullName.split(" ").filter(Boolean).map(n => n[0]).join("").toUpperCase() || "?";

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">My Profile</h1>
        <p className="text-sm text-muted-foreground">Manage your account, security, and preferences</p>
      </div>

      {/* ── Avatar + identity ── */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-primary text-2xl font-bold text-primary-foreground select-none">
              {initials}
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-base truncate">{fullName || "—"}</p>
              <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
              <Badge variant="secondary" className="mt-1 capitalize text-xs">{roleLabel}</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Personal Information ── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <User className="h-4 w-4" /> Personal Information
          </CardTitle>
          <CardDescription className="text-xs">Your name and phone number visible to administrators</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="fullName" className="text-xs">Full Name *</Label>
            <Input
              id="fullName"
              placeholder="Your full name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="h-9"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs flex items-center gap-1">
              <Mail className="h-3 w-3" /> Email Address
            </Label>
            <Input value={user?.email ?? ""} disabled className="h-9 bg-muted text-muted-foreground cursor-not-allowed" />
            <p className="text-[11px] text-muted-foreground">To change your email address use the section below.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone" className="text-xs flex items-center gap-1">
              <Phone className="h-3 w-3" /> Phone Number
              <span className="text-muted-foreground ml-1">(optional)</span>
            </Label>
            <Input
              id="phone"
              type="tel"
              placeholder="+1 604-555-0100"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="h-9"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs flex items-center gap-1">
              <Shield className="h-3 w-3" /> Role
            </Label>
            <Input value={roleLabel} disabled className="h-9 bg-muted text-muted-foreground cursor-not-allowed capitalize" />
            <p className="text-[11px] text-muted-foreground">Assigned by your strata administrator — cannot be self-changed.</p>
          </div>

          <Button size="sm" onClick={handleSaveProfile} disabled={isSavingProfile || !fullName.trim()}>
            {isSavingProfile ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
            Save Changes
          </Button>
        </CardContent>
      </Card>

      {/* ── Change Email ── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Mail className="h-4 w-4" /> Change Email Address
          </CardTitle>
          <CardDescription className="text-xs">
            A confirmation link will be sent to your new address. Your email won't change until you click it.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {emailChangeSent ? (
            <div className="flex flex-col items-center gap-2 py-2 text-center">
              <CheckCircle2 className="h-8 w-8 text-green-500" />
              <p className="text-sm font-medium">Confirmation sent!</p>
              <p className="text-xs text-muted-foreground">Check your new email inbox for the confirmation link.</p>
              <Button variant="ghost" size="sm" className="text-xs mt-1" onClick={() => setEmailChangeSent(false)}>
                Try a different address
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="newEmail" className="text-xs">New Email Address</Label>
                <Input
                  id="newEmail"
                  type="email"
                  placeholder="newemail@example.com"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="h-9"
                  disabled={isChangingEmail}
                />
              </div>
              <Button size="sm" onClick={handleChangeEmail} disabled={isChangingEmail || !newEmail || newEmail === user?.email}>
                {isChangingEmail ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Mail className="mr-1 h-4 w-4" />}
                Send confirmation
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Change Password ── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Lock className="h-4 w-4" /> Change Password
          </CardTitle>
          <CardDescription className="text-xs">Choose a strong password with at least 8 characters.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="newPassword" className="text-xs">New Password</Label>
            <div className="relative">
              <Input
                id="newPassword"
                type={showNew ? "text" : "password"}
                placeholder="At least 8 characters"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="h-9 pr-9"
              />
              <button type="button" tabIndex={-1}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => setShowNew(v => !v)}
              >
                {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {newPassword && (
              <div className="space-y-1">
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map(i => (
                    <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= strength.score ? strength.color : "bg-muted"}`} />
                  ))}
                </div>
                <p className={`text-[11px] font-medium ${strength.score <= 1 ? "text-red-500" : strength.score <= 3 ? "text-amber-500" : "text-green-600"}`}>
                  {strength.label}
                </p>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-xs">Confirm New Password</Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirm ? "text" : "password"}
                placeholder="Repeat new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="h-9 pr-9"
              />
              <button type="button" tabIndex={-1}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => setShowConfirm(v => !v)}
              >
                {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {confirmPassword && newPassword !== confirmPassword && (
              <p className="text-[11px] text-destructive">Passwords do not match</p>
            )}
            {confirmPassword && newPassword === confirmPassword && newPassword.length >= 8 && (
              <p className="text-[11px] text-green-600 flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" /> Passwords match
              </p>
            )}
          </div>

          <Button
            size="sm"
            onClick={handleChangePassword}
            disabled={isSavingPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword || newPassword.length < 8}
          >
            {isSavingPassword ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Lock className="mr-1 h-4 w-4" />}
            Update Password
          </Button>
        </CardContent>
      </Card>

      {/* ── Appearance ── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Sun className="h-4 w-4" /> Appearance
          </CardTitle>
          <CardDescription className="text-xs">Choose how Stratakey looks on this device.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-3">
            {([
              { value: "light",  label: "Light",  Icon: Sun     },
              { value: "dark",   label: "Dark",   Icon: Moon    },
              { value: "system", label: "System", Icon: Monitor },
            ] as const).map(({ value, label, Icon }) => (
              <button
                key={value}
                onClick={() => setTheme(value)}
                className={`flex flex-col items-center gap-2 rounded-lg border p-4 text-sm font-medium transition-colors hover:bg-muted ${
                  theme === value
                    ? "border-primary bg-primary/5 text-primary"
                    : "border-border text-muted-foreground"
                }`}
              >
                <Icon className="h-5 w-5" />
                {label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ── Account & Security ── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Shield className="h-4 w-4" /> Account & Security
          </CardTitle>
          <CardDescription className="text-xs">Review your account details and manage active sessions.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg bg-muted p-3 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted-foreground">Account email</span>
              <span className="text-xs font-medium">{user?.email}</span>
            </div>
            <Separator />
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted-foreground">Role</span>
              <span className="text-xs font-medium capitalize">{roleLabel}</span>
            </div>
          </div>

          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium">Sign out everywhere</p>
              <p className="text-xs text-muted-foreground">Ends all active sessions on all devices immediately.</p>
            </div>
            <Button variant="outline" size="sm" onClick={handleSignOutEverywhere} className="shrink-0 gap-1.5 text-xs">
              <LogOut className="h-3.5 w-3.5" /> Sign out all
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ── Danger Zone ── */}
      <Card className="border-destructive/40">
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2 text-destructive">
            <Trash2 className="h-4 w-4" /> Danger Zone
          </CardTitle>
          <CardDescription className="text-xs">Irreversible actions. Please read carefully before proceeding.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium">Delete my account</p>
              <p className="text-xs text-muted-foreground">Permanently removes your account and all associated data.</p>
            </div>
            <AlertDialog onOpenChange={(open) => { if (!open) setDeleteConfirm(""); }}>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm" className="shrink-0 gap-1.5 text-xs">
                  <Trash2 className="h-3.5 w-3.5" /> Delete account
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete your account permanently?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This cannot be undone. All your data, bookings, and settings will be erased.
                    <br /><br />
                    Type <strong>DELETE</strong> below to confirm.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <Input
                  placeholder="Type DELETE to confirm"
                  value={deleteConfirm}
                  onChange={(e) => setDeleteConfirm(e.target.value)}
                  className="mt-1"
                />
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDeleteAccount}
                    disabled={deleteConfirm !== "DELETE" || isDeleting}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Delete my account
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
